import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { openStripeElementModal, spinnerHtml } from "../utils/stripe_element_modal"

// Canal partagé avec les pages de retour Stripe (ledger_payment_links/success|cancel), qui
// s'ouvrent dans l'onglet du paiement et se referment toutes seules.
export const LEDGER_PAYMENTS_CHANNEL = "depozen:ledger-payments"

const CLOSED_POLL_MS = 500
// Laisse voir la confirmation avant de refermer la modale.
const MODAL_CLOSE_DELAY_MS = 1200

// Bouton « Régler » de Mes paiements.
// - Par défaut : Payment Element dans une modale, sans quitter la page
//   (LedgerSettlementsController). Paiement confirmé → encaissement, puis mise à jour en morph.
// - Repli (Stripe.js absent) : la page Stripe hébergée dans un autre onglet, suivie par
//   BroadcastChannel (pages de retour) et par `closed` sur la fenêtre ouverte.
// Dans les deux cas : « en cours » dès le clic, « reçu » à la fin, « Régler » si abandon.
export default class extends Controller {
  static targets = ["button", "amount"]
  static values = {
    insurancePolicyId: String,
    sessionUrl: String,
    pendingLabel: String,
    processingLabel: String,
    paidLabel: String,
    modalTitle: String,
    summaryLabel: String,
    submitLabel: String,
    closeLabel: String,
    errorLabel: String
  }

  connect() {
    this.buttonHtml = this.hasButtonTarget ? this.buttonTarget.innerHTML : null
    if ("BroadcastChannel" in window) {
      this.channel = new BroadcastChannel(LEDGER_PAYMENTS_CHANNEL)
      this.channel.onmessage = (event) => this.receive(event.data)
    }
  }

  disconnect() {
    this.stopWatching()
    this.channel?.close()
    // La modale n'est pas nettoyée ici : le refresh diffusé par l'encaissement retire cette
    // cellule pendant que la modale affiche encore la confirmation.
  }

  open(event) {
    if (this.state === "pending") {
      event.preventDefault()
      this.paymentWindow?.focus()
      return
    }

    if (this.sessionUrlValue && typeof window.Stripe === "function") {
      event.preventDefault()
      this.openModal()
      return
    }

    this.openWindow(event)
  }

  receive(message) {
    if (!message || String(message.insurancePolicyId) !== this.insurancePolicyIdValue) return

    switch (message.status) {
      case "success":
        this.setState("paid")
        this.refresh()
        break
      case "pending":
        this.setState("processing")
        this.refresh()
        break
      case "cancel":
        this.reset()
        break
    }
  }

  // --- Modale (Payment Element)

  async openModal() {
    this.setState("pending")
    const settlement = { completed: false, ledgerPaymentId: null }
    const modal = openStripeElementModal({
      title: this.modalTitleValue,
      closeLabel: this.closeLabelValue,
      submitLabel: this.submitLabelValue,
      onHidden: () => this.modalHidden(settlement)
    })
    settlement.modal = modal

    let data
    try {
      data = await this.postJson(this.sessionUrlValue, { insurance_policy_id: this.insurancePolicyIdValue })
    } catch (error) {
      modal.showError(error.message)
      return
    }
    settlement.ledgerPaymentId = data.ledger_payment_id
    // Modale fermée pendant la création du paiement : on le jette aussitôt.
    if (modal.hidden) return this.abandon(settlement)

    const stripe = window.Stripe(data.publishable_key, data.stripe_account ? { stripeAccount: data.stripe_account } : {})
    const elements = modal.mount({
      stripe,
      elementsOptions: {
        clientSecret: data.client_secret,
        ...(data.customer_session_client_secret && { customerSessionClientSecret: data.customer_session_client_secret }),
        locale: document.documentElement.lang || "fr"
      },
      paymentElementOptions: { defaultValues: { billingDetails: { email: data.email || "" } } },
      summary: { label: data.reference ? `${this.summaryLabelValue} ${data.reference}` : this.summaryLabelValue, amount: data.amount }
    })

    modal.onSubmit(async () => {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
        // Seulement pour un moyen à redirection ; carte et SEPA restent dans la modale.
        confirmParams: { return_url: window.location.href }
      })
      if (error) throw new Error(error.message)

      if (!["succeeded", "processing"].includes(paymentIntent?.status)) throw new Error(this.errorLabelValue)
      await this.completed(settlement)
    })
  }

  async completed(settlement) {
    settlement.completed = true
    let status = "pending"
    try {
      const data = await this.postJson(this.completeUrl(settlement.ledgerPaymentId), {})
      status = data.status
    } catch {
      // Stripe a confirmé côté client : l'encaissement suivra par webhook.
    }

    const paid = status === "success"
    this.setState(paid ? "paid" : "processing")
    settlement.modal.showSuccess(paid ? this.paidLabelValue : this.processingLabelValue)
    setTimeout(() => settlement.modal.hide(), MODAL_CLOSE_DELAY_MS)
    this.refresh()
  }

  modalHidden(settlement) {
    if (settlement.completed) return

    this.abandon(settlement)
    this.reset()
  }

  abandon(settlement) {
    if (!settlement.ledgerPaymentId) return

    // keepalive : la page peut partir juste après la fermeture.
    fetch(this.abandonUrl(settlement.ledgerPaymentId), {
      method: "POST",
      headers: this.jsonHeaders(),
      keepalive: true
    }).catch(() => {})
  }

  completeUrl(ledgerPaymentId) {
    return `${this.sessionUrlValue}/${encodeURIComponent(ledgerPaymentId)}/complete`
  }

  abandonUrl(ledgerPaymentId) {
    return `${this.sessionUrlValue}/${encodeURIComponent(ledgerPaymentId)}/abandon`
  }

  async postJson(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify(body)
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || this.errorLabelValue)
    return data
  }

  jsonHeaders() {
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
    }
  }

  // --- Repli : page Stripe hébergée dans un autre onglet

  openWindow(event) {
    const paymentWindow = window.open(this.buttonTarget.href, "_blank")
    // Bloqué (popup blocker) : on laisse le lien s'ouvrir normalement, sans suivi.
    if (!paymentWindow) return

    event.preventDefault()
    // Encore en about:blank, donc même origine : on coupe `opener` avant la navigation vers
    // Stripe (équivalent de rel=noopener). `closed` reste lisible sur notre référence.
    paymentWindow.opener = null
    this.paymentWindow = paymentWindow
    this.setState("pending")
    this.watchWindow()
  }

  watchWindow() {
    this.stopWatching()
    this.closedPoll = setInterval(() => {
      if (!this.paymentWindow?.closed) return

      this.stopWatching()
      // Fermé sans message de retour : abandon (ou page de retour jamais atteinte).
      if (this.state === "pending") this.reset()
    }, CLOSED_POLL_MS)
  }

  stopWatching() {
    if (this.closedPoll) clearInterval(this.closedPoll)
    this.closedPoll = null
  }

  // --- État du bouton

  reset() {
    this.stopWatching()
    this.paymentWindow = null
    this.setState(null)
  }

  refresh() {
    // L'encaissement est déjà enregistré : la page serveur est à jour.
    // Morph + scroll conservé : seuls le solde, l'échéancier et le bandeau changent (les frames
    // de la page portent refresh="morph"). Turbo ignore ce refresh si le broadcast serveur en a
    // déjà lancé un.
    Turbo.session.refresh(document.baseURI, { method: "morph", scroll: "preserve" })
  }

  setState(state) {
    this.state = state
    this.element.dataset.ledgerSettleState = state || ""
    if (!this.hasButtonTarget) return

    const button = this.buttonTarget
    button.classList.toggle("disabled", state === "pending")
    button.setAttribute("aria-busy", state === "pending" ? "true" : "false")

    switch (state) {
      case "pending":
        button.innerHTML = `${spinnerHtml("me-1")}${this.textHtml(this.pendingLabelValue)}`
        break
      case "processing":
        this.replaceWithStatus("fa-solid fa-clock", this.processingLabelValue, "text-warning")
        break
      case "paid":
        this.replaceWithStatus("fa-solid fa-circle-check", this.paidLabelValue, "text-success")
        break
      default:
        button.innerHTML = this.buttonHtml
    }
  }

  replaceWithStatus(icon, label, className) {
    const status = document.createElement("span")
    status.className = `small fw-semibold text-nowrap ${className}`
    status.innerHTML = this.labelHtml(icon, label)
    this.buttonTarget.replaceWith(status)
    if (this.hasAmountTarget) this.amountTarget.classList.add("d-none")
    this.stopWatching()
  }

  labelHtml(icon, label) {
    return `<i class="${icon} me-1" aria-hidden="true"></i>${this.textHtml(label)}`
  }

  textHtml(label) {
    const text = document.createElement("span")
    text.textContent = label
    return text.outerHTML
  }
}
